/** FASE 6A · Jun 2026 — Gastos bancarios, flujo de caja y resultado operativo estimado (solo Admin). */
import { useEffect, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { useI18n } from "@/context/I18nContext";
import { DeltaRow } from "@/pages/panels/FinancePanel";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

const COLORS = ["#111111", "#0F7B3E", "#B8860B", "#C5221F", "#4A5568", "#805AD5", "#2B6CB0", "#D69E2E", "#718096", "#38A169", "#9B2C2C"];

const SYSTEM_CATS = ["materiales", "publicidad", "software_suscripciones", "servicios", "renta",
  "nomina", "impuestos", "transporte", "comisiones_bancarias", "sales_tax_remitido", "otros"];

export const ExpenseSummary = ({ period, start, end }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (period === "custom" && (!start || !end)) return;
    const params = { period };
    if (period === "custom") { params.start = start; params.end = end; }
    axios.get(`${API}/api/finance/expenses`, { params }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [period, start, end]);

  if (!data) return null;
  const c = data.current;
  const prev = data.previous;
  const yoy = data.yoy;
  const catLabel = (k) => (SYSTEM_CATS.includes(k) ? t(`fin_bi_c_${k}`) : k);
  const cats = Object.entries(c.by_category).filter(([, v]) => v !== 0).sort((a, b) => b[1] - a[1]);

  const kpis = [
    { k: "fin_ex_gross", v: money(c.gross_bank_expenses), id: "fin-ex-gross" },
    { k: "fin_ex_refunds", v: money(c.expense_refunds), id: "fin-ex-refunds" },
    { k: "fin_ex_net", v: money(c.net_bank_expenses), id: "fin-ex-net",
      cmp: { cur: c.net_bank_expenses, prev: prev.net_bank_expenses, yoy: yoy.net_bank_expenses, inverse: true } },
    { k: "fin_ex_count", v: `${c.counted_movements}`, id: "fin-ex-count" },
    { k: "fin_ex_materials", v: money(c.materials_paid), id: "fin-ex-materials" },
    { k: "fin_ex_equipment", v: money(c.equipment_paid), id: "fin-ex-equipment" },
    { k: "fin_ex_tax_remitted", v: money(c.sales_tax_remitted), id: "fin-ex-tax-remitted" },
    { k: "fin_ex_operating", v: money(c.operating_expenses), id: "fin-ex-operating",
      cmp: { cur: c.operating_expenses, prev: prev.operating_expenses, yoy: yoy.operating_expenses, inverse: true } },
    { k: "fin_ex_cashflow", v: money(c.net_cash_flow), id: "fin-ex-cashflow",
      cmp: { cur: c.net_cash_flow, prev: prev.net_cash_flow, yoy: yoy.net_cash_flow } },
    { k: "fin_ex_result", v: money(c.estimated_operating_result), id: "fin-ex-result",
      cmp: { cur: c.estimated_operating_result, prev: prev.estimated_operating_result, yoy: yoy.estimated_operating_result } },
  ];

  return (
    <div data-testid="fin-expenses" className="space-y-4">
      <div className="label-overline">{t("fin_ex_title")}</div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <div key={k.id} data-testid={k.id} className="border border-black bg-white p-4">
            <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">
              {t(k.k)}{k.est ? ` · ${t("fin_estimated")}` : ""}
            </div>
            <div className="font-display text-xl font-black mt-1">{k.v}</div>
            {k.cmp && (
              <div className="mt-1.5 space-y-0.5">
                <DeltaRow id={`${k.id}-vs-prev`} label={t("fin_vs_prev")} cur={k.cmp.cur} prev={k.cmp.prev} inverse={k.cmp.inverse} />
                {!data.yoy_same_as_previous && (
                  <DeltaRow id={`${k.id}-vs-yoy`} label={t("fin_vs_yoy")} cur={k.cmp.cur} prev={k.cmp.yoy} inverse={k.cmp.inverse} />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div data-testid="fin-ex-cashflow-note" className="border-l-4 border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2 text-xs text-[var(--text-secondary)]">
        {t("fin_ex_cashflow_note")}
      </div>
      <div data-testid="fin-ex-excluded-note" className="border-l-4 border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2 text-xs text-[var(--text-secondary)]">
        {t("fin_ex_excluded_note")}
      </div>
      {!c.profit_complete && (
        <div data-testid="fin-ex-overstated" className="border-l-4 border-[#B8860B] bg-[#FDF6E3] px-4 py-2 text-xs">
          {t("fin_ex_overstated")}
        </div>
      )}
      <div data-testid="fin-ex-disclaimer" className="border-l-4 border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2 text-xs text-[var(--text-secondary)]">
        {t("fin_ex_disclaimer")}
      </div>

      {c.counted_movements === 0 ? (
        <div data-testid="fin-ex-empty" className="border border-[var(--border)] bg-white p-6 text-sm italic text-[var(--text-secondary)]">
          {t("fin_ex_empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div data-testid="fin-ex-donut" className="border border-[var(--border)] bg-white p-4">
            <div className="label-overline mb-2">{t("fin_ex_by_category")}</div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={cats.map(([k, v]) => ({ name: catLabel(k), value: v }))} dataKey="value" nameKey="name"
                    innerRadius="50%" outerRadius="85%" strokeWidth={1}>
                    {cats.map(([k], i) => <Cell key={k} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="font-mono-tech text-[11px] space-y-0.5 mt-2">
              {cats.map(([k, v], i) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="inline-block w-2.5 h-2.5 shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                  <span>{catLabel(k)}: <b>{money(v)}</b></span>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="fin-ex-trend" className="lg:col-span-2 border border-[var(--border)] bg-white p-4">
            <div className="label-overline mb-2">{t("fin_ex_trend")}</div>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={c.trend.map((s) => ({ ...s, label: c.granularity === "month" ? s.bucket : s.bucket.slice(5) }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Bar dataKey="net_bank_expenses" name={t("fin_ex_net")} fill="#C5221F" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
